import { InMemoryCache } from '@apollo/client/cache'
import { ApolloClient, from } from '@apollo/client/core/index.js'
import type { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import type { Client } from 'graphql-ws'

import createClientLinks from './createClientLinks'
import SubscriptionDebug from './SubscriptionDebug'
import { $RootStore } from '../../src/stores/RootStore'
import { Sdk, getSdkApollo } from './requester'
import analytics from '../../src/stores/analytics'
import { runInAction } from 'mobx'

export interface $CommunicationConfig {
  apiUrl: string
  graphUrl: string
  wsUrl?: string
  timezone: string
  platform: string
  env: string
  addNetworkLatency: number
  deviceip?: string
  resubscribeActiveListeners?: () => Promise<void>
}

class Communication {
  rootStore: $RootStore
  client: ApolloClient
  wsLink?: GraphQLWsLink
  wsClient?: Client
  config: $CommunicationConfig
  latestErrorCode: string | undefined
  requester: Sdk
  subscriptionDebug = new SubscriptionDebug()

  constructor(rootStore: $RootStore, config: $CommunicationConfig) {
    this.rootStore = rootStore

    analytics.track('Connecting to API', {
      apiUrl: config.apiUrl,
      graphUrl: config.graphUrl,
      wsUrl: config.wsUrl,
    })

    const { links, wsLink, wsClient } = createClientLinks(rootStore, config, this.subscriptionDebug)
    this.config = config
    this.wsLink = wsLink
    this.wsClient = wsClient
    this.attachSubscriptionDebugHandlers(wsClient)

    this.client = new ApolloClient({
      link: from(links),
      cache: new InMemoryCache({
        dataIdFromObject: (object: any) => object._id ? `${object.__typename}_${object._id}` : '',
      }),
      defaultOptions: {
        query: { fetchPolicy: 'network-only' },
      },
    })

    this.requester = getSdkApollo(
      this.client,
      {
        dev: config.env === 'development',
        manageError: this.rootStore.errorHandler.manageRawError,
        rootStore: this.rootStore,
        addNetworkLatency: this.config.addNetworkLatency,
      },
    )
  }

  closeWSLink = () => {
    Promise.resolve(this.wsClient?.dispose()).catch(() => {})
  }

  forceWsReconnect = () => {
    if (!this.wsClient) return

    this.subscriptionDebug.logEvent({
      kind: 'ws',
      name: 'manual',
      detail: 'force reconnect',
    })
    this.wsClient.terminate()
  }

  resubscribeActiveListeners = async () => {
    this.subscriptionDebug.logEvent({
      kind: 'ws',
      name: 'manual',
      detail: 'resubscribe active listeners',
    })

    await this.config.resubscribeActiveListeners?.()
  }

  private attachSubscriptionDebugHandlers = (wsClient?: Client) => {
    if (!wsClient) {
      this.subscriptionDebug.setWsStatus('unavailable')
      return
    }

    const { subscriptionDebug } = this

    wsClient.on('connecting', isRetry => {
      runInAction(() => subscriptionDebug.setWsStatus(isRetry ? 'reconnecting' : 'connecting'))
      subscriptionDebug.logEvent({ kind: 'ws', name: isRetry ? 'reconnecting' : 'connecting' })
    })

    wsClient.on('connected', () => {
      runInAction(() => {
        subscriptionDebug.setWsStatus('connected')
        subscriptionDebug.setLastWsError(null)
      })
      subscriptionDebug.logEvent({ kind: 'ws', name: 'connected' })
    })

    wsClient.on('closed', () => {
      runInAction(() => subscriptionDebug.setWsStatus('disconnected'))
      subscriptionDebug.logEvent({ kind: 'ws', name: 'disconnected' })
    })

    wsClient.on('error', error => {
      const message = error instanceof Error ? error.message : String(error)
      runInAction(() => {
        subscriptionDebug.setWsStatus('error')
        subscriptionDebug.setLastWsError(message || 'WebSocket error')
      })
      subscriptionDebug.logEvent({
        kind: 'error',
        name: 'websocket',
        detail: message || 'WebSocket error',
      })
    })
  }
}

export default Communication
